import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';

/**
 * Master-Admin "Grace Accounts" — manual linking of an existing Grace account
 * (and its existing cards) to a Lendox party.
 *
 * Matches the backend contract exactly:
 *   POST /services/embedded/api/master-admin/grace-accounts/map
 *     body  → MapGraceAccountRequest  { partyId, graceAccountId }
 *     200   → MapGraceAccountResult
 *
 * Nothing is created in Grace — the existing Grace account + cards are linked to
 * the party. Authority: ROLE_MASTER_ADMIN.
 */
export interface MapGraceAccountRequest {
  /** The Lendox Party id to link the Grace account to. */
  partyId: number;
  /** The Grace account id (from the Grace system) to link. */
  graceAccountId: string;
}

export interface MapGraceAccountResult {
  /** The Lendox payment-account id that now references the Grace account. */
  paymentAccountId: number;
  /** Kind of account that was linked (e.g. "SME", "LENDER"). */
  accountKind: string;
  /** Whether the party's external id was (re)linked onto the Grace owner. */
  ownerExternalIdLinked: boolean;
  /** Ids of the Grace cards that were linked to the party. */
  cardIds: number[];
  /** Number of cards linked (== cardIds.length). */
  cardCount: number;
  /** Human-readable result summary from the backend. */
  message: string;
}

/* ---------------------------------------------------------------------------
 * EM-1042 / EM-1043 (K3 / K4, epic EM-915 — FR-9.2) — read-only Grace linkage.
 *
 *   GET /services/embedded/api/master-admin/grace-accounts/linkage/{partyId}
 *     200 → GraceLinkage
 *
 * Support/reconciliation view of a party's Grace structure. Read-only by design:
 * there is no mutating counterpart, and the page exposes no action beyond
 * navigation and copy-to-clipboard.
 * ------------------------------------------------------------------------- */

/** How a party's Grace accounts are owned. The single most useful triage fact. */
export type GraceOwnershipShape = 'BUSINESS_OWNED' | 'PERSON_OWNED' | 'NOT_LINKED';

/**
 * EM-990 / I7 — which enrollment model a NEW activation provisions (`grace.enrollment-model`).
 * `BUSINESS_GAM` is the epic's target ladder; `ACCOUNT_ONLY` provisions the business user and the
 * business-owned account and skips the GAM rungs, for environments where the open Grace KYB /
 * initiator questions block them.
 */
export type GraceEnrollmentModel = 'BUSINESS_GAM' | 'ACCOUNT_ONLY';

export interface GraceBusinessLink {
  graceBusinessUserId: string;
  externalId: string | null;
  legalEntityName: string | null;
  /** Grace KYB state — KYB_DONE, MANUAL_REVIEW, ADR, … */
  kybState: string | null;
  /** False when the id is recorded locally but Grace does not resolve it. */
  reachable: boolean;
}

export interface GracePersonLink {
  graceUserId: string;
  externalId: string | null;
  displayName: string | null;
  /** Grace KYC state — an unfinished KYC is the likeliest cause of stalled provisioning. */
  kycState: string | null;
  reachable: boolean;
}

export interface GraceGamLink {
  graceGamId: string | null;
  representativeId: number | null;
  name: string | null;
  workingPosition: string | null;
  /** A GAM is declared before it is linked to a real Grace person. */
  linked: boolean;
  person: GracePersonLink | null;
  /** Lendox records this rep as a GAM but Grace lists no matching entry. */
  localOnly: boolean;
}

export interface GraceDisponentLink {
  disponentId: string | null;
  graceUserId: string | null;
  representativeId: number | null;
  displayName: string | null;
  isGam: boolean;
  /** Local roles whose union defines the permission bundle synced to Grace. */
  roles: string[];
}

export interface GraceCardLink {
  graceCardId: string | null;
  maskedPan: string | null;
  status: string | null;
  cardholderUserId: string | null;
  cardholderName: string | null;
  representativeId: number | null;
  /** Legacy derived cardholder id with no real person behind it — label it, never render as a name. */
  cardholderSynthetic: boolean;
  /**
   * The FIRST line Grace actually embosses on the card (EM-1814) — the printed cardholder name, read
   * back from the issuer's card personalisation. `null` when Grace reports no personalisation.
   */
  embossedCardholderLine: string | null;
  /**
   * The SECOND embossed line — the company / trade line under the cardholder's name, which on a
   * Track-2 dossier is the "Name on Card" the applicant typed at Stage 2 (EM-1814).
   */
  embossedNameOnCard: string | null;
}

export interface GraceAccountLink {
  graceAccountId: string | null;
  paymentAccountId: number | null;
  iban: string | null;
  localStatus: string | null;
  graceState: string | null;
  accountOwnerId: string | null;
  businessOwned: boolean;
  disponents: GraceDisponentLink[];
  cards: GraceCardLink[];
  reachable: boolean;
}

export interface GraceLinkage {
  partyId: number;
  partyExternalId: string | null;
  legalName: string | null;
  cui: string | null;
  ownershipShape: GraceOwnershipShape;
  /**
   * EM-990 / I7 — the `grace.enrollment-model` rollout flag currently in force. It describes what a
   * NEW activation would provision now, deliberately NOT what this party was provisioned under
   * (`ownershipShape` and `gams` already answer that). Rendered beside the shape so an operator can
   * tell "no GAM because the flag is ACCOUNT_ONLY" apart from "no GAM because a rung failed".
   */
  configuredEnrollmentModel: GraceEnrollmentModel;
  business: GraceBusinessLink | null;
  personOwner: GracePersonLink | null;
  gams: GraceGamLink[];
  accounts: GraceAccountLink[];
  /** Non-fatal observations — the view degrades to partial rather than to an error. */
  warnings: string[];
}

/** Contract for the Grace Accounts data service. */
export interface GraceAccountsApi {
  /**
   * Link an existing Grace account (+ its existing cards) to a Lendox party.
   * Authority: ROLE_MASTER_ADMIN.
   */
  map(req: MapGraceAccountRequest): Observable<MapGraceAccountResult>;

  /**
   * Read-only inspection of a party's complete Grace linkage.
   * Authority: ROLE_MASTER_ADMIN.
   */
  linkage(partyId: number): Observable<GraceLinkage>;
}

/** DI token for the Grace Accounts data service. */
export const GRACE_ACCOUNTS = new InjectionToken<GraceAccountsApi>('GRACE_ACCOUNTS');
